import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { get, ref, remove, set } from 'firebase/database';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBookOpen,
  FaCheckCircle,
  FaClock,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFileDownload,
  FaLink,
  FaPaperclip,
  FaRegCircle,
  FaVideo,
} from 'react-icons/fa';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { useAdminCoursesOverview, useStudentCourses } from '../../hooks/useLmsData';
import {
  buildReaderBlocks,
  findLessonNeighborsForLesson,
  formatSessionMeta,
  getLessonHeroImage,
  lessonDisplayTitle,
  lessonReaderUrlSegment,
  localizeLesson,
  sortLessonsBySession,
} from '../../utils/lessonReader';
import {
  getYouTubeEmbedUrl,
  normalizeLessonAttachments,
  normalizeLessonVideos,
} from '../../utils/lessonMedia';
import { buildScheduledLessons } from '../../utils/studentSchedule';
import {
  getCourseSuspension,
  isLessonRestrictedBySuspension,
  suspensionMessage,
} from '../../utils/accessSuspensions';

const READER_LABEL_KEYS = [
  'reader.tutorialRibbon',
  'reader.beforeYouBegin',
  'reader.wrapUp',
  'reader.facilitatorOutline',
  'reader.facilitatorHint',
  'reader.aboutSession',
  'reader.whatYouCover',
  'reader.learningOutcomes',
  'reader.sessionFlow',
  'reader.materials',
  'reader.activities',
  'reader.safety',
  'reader.assessment',
  'reader.afterSession',
  'reader.tutorNotes',
  'reader.furtherReading',
  'reader.sessionVideos',
  'reader.sessionAttachments',
  'reader.openAttachment',
];

const LessonReaderPage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { language, t } = useLanguage();
  const isStudent = currentUser?.role === 'student';
  const isAdmin = currentUser?.role === 'admin';
  const fromMyCourses = location.pathname.includes('/courses/my-courses/');

  const studentData = useStudentCourses(isStudent ? currentUser?.uid : null, {
    enabled: isStudent,
  });
  const adminOverview = useAdminCoursesOverview({
    enabled: isAdmin && !fromMyCourses,
  });
  const [course, setCourse] = useState(null);
  const [staffLoading, setStaffLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const sortedAdminCourses = useMemo(() => {
    const list = adminOverview.data?.courseList;
    if (!Array.isArray(list) || !isAdmin || fromMyCourses) return [];
    return [...list].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [adminOverview.data?.courseList, isAdmin, fromMyCourses]);

  const showAdminCourseBar =
    isAdmin && !fromMyCourses && sortedAdminCourses.length > 0 && !adminOverview.loading;

  const adminCourseMeta = useMemo(
    () => sortedAdminCourses.find((c) => c.id === courseId),
    [sortedAdminCourses, courseId],
  );

  useEffect(() => {
    if (isStudent) {
      if (studentData.loading) return;
      const c = studentData.data?.courses?.[courseId];
      if (c) {
        setCourse({ id: courseId, ...c });
        setFetchError('');
      } else {
        setCourse(null);
        setFetchError('Course not found or not available.');
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setStaffLoading(true);
        setFetchError('');
        const snap = await get(ref(db, `courses/${courseId}`));
        if (cancelled) return;
        if (!snap.exists()) {
          setCourse(null);
          setFetchError('Course not found.');
          return;
        }
        setCourse({ id: courseId, ...snap.val() });
      } catch {
        if (!cancelled) setFetchError('Failed to load course.');
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, isStudent, studentData.data, studentData.loading]);

  const enrollment = useMemo(() => {
    if (!isStudent || !studentData.data?.enrollments) return null;
    return studentData.data.enrollments.find((e) => e.courseId === courseId);
  }, [isStudent, studentData.data, courseId]);

  const [completedLessons, setCompletedLessons] = useState({});

  useEffect(() => {
    if (enrollment?.completedLessons) {
      setCompletedLessons(enrollment.completedLessons);
    }
  }, [enrollment?.completedLessons]);

  const lessons = useMemo(
    () => (Array.isArray(course?.lessons) ? course.lessons : []),
    [course?.lessons],
  );
  const lesson = useMemo(() => {
    if (!lessons.length) return null;
    const byId = lessons.find(
      (l) => l.id === lessonId || String(l.id) === String(lessonId),
    );
    if (byId) return byId;
    const n = parseInt(lessonId, 10);
    if (!Number.isNaN(n) && lessons[n] != null) return lessons[n];
    return null;
  }, [lessons, lessonId]);

  const { prev, next, index, sorted } = useMemo(
    () => findLessonNeighborsForLesson(lessons, lesson),
    [lessons, lesson],
  );
  const scheduledLessons = useMemo(
    () => buildScheduledLessons({ course, enrollment }),
    [course, enrollment],
  );
  const currentScheduledLesson = useMemo(() => {
    if (!lesson) return null;
    return (
      scheduledLessons.find(
        (item, scheduledIndex) =>
          (lesson.id != null && item.id != null
            ? String(item.id) === String(lesson.id)
            : scheduledIndex === index),
      ) || null
    );
  }, [scheduledLessons, lesson, index]);
  const courseSuspension = useMemo(
    () =>
      getCourseSuspension({
        courseId,
        user: studentData.data?.user || currentUser,
        student: studentData.data?.student,
        enrollment,
        course,
      }),
    [course, courseId, currentUser, enrollment, studentData.data],
  );

  const handleAdminCourseSwitch = useCallback(
    (newCourseId) => {
      if (!newCourseId || newCourseId === courseId) return;
      const meta = sortedAdminCourses.find((c) => c.id === newCourseId);
      const raw = Array.isArray(meta?.lessons) ? meta.lessons : [];
      const sortedTarget = sortLessonsBySession(raw);
      if (!sortedTarget.length) {
        navigate(`/courses/${newCourseId}`);
        return;
      }
      let targetLesson = null;
      if (lesson?.sessionNumber != null && lesson.sessionNumber !== '') {
        targetLesson = sortedTarget.find(
          (l) => Number(l.sessionNumber) === Number(lesson.sessionNumber),
        );
      }
      if (!targetLesson && index >= 0) {
        targetLesson = sortedTarget[index] ?? null;
      }
      if (!targetLesson) {
        targetLesson = sortedTarget[0];
      }
      const seg = lessonReaderUrlSegment(raw, targetLesson);
      navigate(`/courses/${newCourseId}/lessons/${seg}`);
    },
    [courseId, index, lesson, navigate, sortedAdminCourses],
  );

  const readerAudience = isAdmin ? 'admin' : 'student';

  const readerLabels = useMemo(
    () =>
      Object.fromEntries(READER_LABEL_KEYS.map((k) => [k, t(k)])),
    [t],
  );

  const displayLesson = useMemo(
    () => (lesson ? localizeLesson(lesson, language) : null),
    [lesson, language],
  );

  const blocks = useMemo(
    () =>
      displayLesson
        ? buildReaderBlocks(displayLesson, {
            audience: readerAudience,
            labels: readerLabels,
          })
        : [],
    [displayLesson, readerAudience, readerLabels],
  );

  const lessonVideoUrls = useMemo(
    () => (lesson ? normalizeLessonVideos(lesson) : []),
    [lesson],
  );
  const lessonAttachments = useMemo(
    () => (lesson ? normalizeLessonAttachments(lesson) : []),
    [lesson],
  );

  const showResourcesSection = Boolean(
    lesson &&
      (lessonVideoUrls.length > 0 ||
        lessonAttachments.length > 0 ||
        (lesson.assignmentUrl && String(lesson.assignmentUrl).trim()) ||
        (lesson.tuteUrl && String(lesson.tuteUrl).trim())),
  );

  const blockNavItems = useMemo(() => {
    const items = blocks
      .filter((b) => b.id && b.title && b.type !== 'tutorial_ribbon')
      .map((b) => ({ id: b.id, title: b.title }));
    if (showResourcesSection) {
      items.push({
        id: 'lesson-resources',
        title: readerLabels['reader.resources'],
      });
    }
    return items;
  }, [blocks, readerLabels, showResourcesSection]);

  const readerFontFamily =
    language === 'si'
      ? '"Noto Sans Sinhala", system-ui, sans-serif'
      : language === 'ta'
        ? '"Noto Sans Tamil", system-ui, sans-serif'
        : undefined;

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const courseProgress = useMemo(() => {
    if (!sorted.length) return { done: 0, total: 0, pct: 0 };
    let done = 0;
    sorted.forEach((l, idx) => {
      const key = l?.id != null && String(l.id).trim() !== '' ? l.id : `${courseId}_lesson_${idx}`;
      const v = completedLessons[key];
      if (v === true || v?.completed) done += 1;
    });
    const total = sorted.length;
    return { done, total, pct: Math.round((done / total) * 100) };
  }, [sorted, completedLessons, courseId]);
  const hero = lesson ? getLessonHeroImage(lesson) : '';
  const meta = lesson ? formatSessionMeta(lesson) : [];
  const lessonTitleUi = displayLesson?.title || lesson?.title || '';

  const canStudy =
    !isStudent ||
    (enrollment?.status === 'active' &&
      (currentScheduledLesson?.isAvailable ?? true) &&
      !isLessonRestrictedBySuspension(
        courseSuspension,
        currentScheduledLesson || lesson,
      ));
  const lessonKey = lesson?.id || `${courseId}_lesson_${index}`;
  const completedVal = completedLessons[lessonKey];
  const isCompleted = completedVal === true || completedVal?.completed;

  const backHref = fromMyCourses
    ? `/courses/my-courses/${courseId}`
    : `/courses/${courseId}`;

  const lessonPathSegment = (l) => lessonReaderUrlSegment(lessons, l);

  const lessonHref = (l) =>
    fromMyCourses
      ? `/courses/my-courses/${courseId}/lessons/${lessonPathSegment(l)}`
      : `/courses/${courseId}/lessons/${lessonPathSegment(l)}`;

  const handleToggleComplete = async () => {
    if (!enrollment?.id || !lesson || !canStudy) return;

    const alreadyCompleted = isCompleted;
    const nextValue = alreadyCompleted
      ? false
      : { completed: true, completedAt: new Date().toISOString() };

    const lessonRef = ref(db, `enrollments/${enrollment.id}/completedLessons/${lessonKey}`);

    if (nextValue === false) {
      await remove(lessonRef);
    } else {
      await set(lessonRef, nextValue);
    }

    const nextCompleted = { ...completedLessons };
    if (nextValue === false) delete nextCompleted[lessonKey];
    else nextCompleted[lessonKey] = nextValue;
    setCompletedLessons(nextCompleted);

    const totalLessons = lessons.length || 1;
    const completedCount = Object.values(nextCompleted).filter(
      (v) => v === true || v?.completed,
    ).length;
    const progress = Math.round((completedCount / totalLessons) * 100);
    await set(ref(db, `enrollments/${enrollment.id}/progress`), progress);
    studentData.refresh?.();
  };

  if (isStudent ? studentData.loading : staffLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (fetchError || !course) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-red-600">{fetchError || 'Course not found.'}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(backHref)}>
          {fromMyCourses ? t('reader.backMyCourse') : t('reader.backCourse')}
        </Button>
      </div>
    );
  }

  if (isStudent && !canStudy) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-gray-700">
          {courseSuspension
            ? suspensionMessage(courseSuspension, course?.name)
            : t('reader.enrollmentRequired')}
        </p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(backHref)}>
          {fromMyCourses ? t('reader.backMyCourse') : t('reader.backCourse')}
        </Button>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-gray-700">{t('reader.notFound')}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(backHref)}>
          {fromMyCourses ? t('reader.backMyCourse') : t('reader.backCourse')}
        </Button>
      </div>
    );
  }

  const sidebar = (
    <div className="space-y-4">
      <Card title={t('reader.cardSession')} className="shadow-sm">
        <div className="space-y-2 text-sm text-slate-600">
          {meta.length ? (
            <div className="flex flex-wrap gap-1.5">
              {meta.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium capitalize text-stone-700"
                >
                  {m.includes('min') ? <FaClock className="text-[10px] opacity-70" /> : null}
                  {m}
                </span>
              ))}
            </div>
          ) : null}
          <p className="font-medium leading-snug text-slate-900">{lessonTitleUi}</p>
          {lesson.sessionNumber != null ? (
            <p className="text-xs text-slate-500">
              {sorted.length
                ? t('reader.sessionOf', {
                    n: lesson.sessionNumber,
                    total: sorted.length,
                  })
                : `Session ${lesson.sessionNumber}`}
            </p>
          ) : null}
        </div>
      </Card>

      {isStudent && canStudy && sorted.length > 0 ? (
        <Card title={t('reader.progressTitle')} className="shadow-sm">
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-slate-600">
              <span>
                {t('reader.progressLine', {
                  done: courseProgress.done,
                  total: courseProgress.total,
                })}
              </span>
              <span className="font-semibold text-slate-800">{courseProgress.pct}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-stone-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500 transition-all duration-300"
                style={{ width: `${courseProgress.pct}%` }}
              />
            </div>
          </div>
        </Card>
      ) : null}

      {blockNavItems.length > 0 ? (
        <Card title={t('reader.onThisPage')} className="shadow-sm">
          <nav
            className="no-scrollbar max-h-52 space-y-1 overflow-y-auto text-sm"
            aria-label="Lesson sections"
          >
            {blockNavItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                className="flex w-full rounded-lg px-2 py-1.5 text-left text-slate-600 transition hover:bg-stone-50 hover:text-pink-800"
              >
                <span className="line-clamp-2">{item.title}</span>
              </button>
            ))}
          </nav>
        </Card>
      ) : null}

      {sorted.length > 1 ? (
        <Card title={t('reader.allSessions')} className="shadow-sm">
          <ol className="no-scrollbar max-h-64 space-y-0.5 overflow-y-auto text-sm">
            {sorted.map((l, i) => {
              const active =
                l.id != null && lesson.id != null
                  ? String(l.id) === String(lesson.id)
                  : l === lesson;
              return (
                <li key={l.id || i}>
                  <Link
                    to={lessonHref(l)}
                    className={`flex items-center justify-between rounded-lg px-2 py-2 transition ${
                      active
                        ? 'bg-pink-50 font-medium text-pink-900'
                        : 'text-slate-600 hover:bg-stone-50'
                    }`}
                  >
                    <span className="line-clamp-2 pr-2">
                      {l.sessionNumber != null ? `${l.sessionNumber}. ` : ''}
                      {lessonDisplayTitle(l, language) || `Session ${i + 1}`}
                    </span>
                    {active ? (
                      <span className="shrink-0 text-xs text-pink-600">{t('reader.here')}</span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ol>
        </Card>
      ) : null}
    </div>
  );

  return (
    <div
      className={`min-h-screen bg-gradient-to-b from-stone-50 via-white to-pink-50/40 pt-2 ${
        showAdminCourseBar ? 'pb-24 sm:pb-28' : 'pb-16'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-stone-200/80 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={backHref}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-pink-700"
            >
              <FaArrowLeft className="text-xs" />
              {fromMyCourses ? t('reader.backMyCourse') : t('reader.backCourse')}
            </Link>
          </div>
          {isAdmin ? (
            <span className="rounded-full bg-pink-100 px-3 py-1 text-xs font-semibold text-pink-900">
              {t('reader.previewAdmin')}
            </span>
          ) : null}
        </header>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)] xl:items-start">
          <aside className="no-scrollbar order-2 space-y-4 xl:sticky xl:top-6 xl:order-1 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto xl:pr-1">
            {sidebar}
          </aside>

          <div className="order-1 min-w-0 xl:order-2">
            <article
              className="overflow-hidden rounded-3xl border border-stone-200/90 bg-white shadow-[0_20px_60px_-24px_rgba(15,23,42,0.18)]"
              style={readerFontFamily ? { fontFamily: readerFontFamily } : undefined}
            >
          {hero ? (
            <div className="relative aspect-[21/9] max-h-[320px] w-full overflow-hidden bg-stone-100 sm:aspect-[2.4/1]">
              <img src={hero} alt="" className="h-full w-full object-cover" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
            </div>
          ) : (
            <div className="flex aspect-[21/9] max-h-[200px] items-center justify-center bg-gradient-to-br from-pink-100 via-white to-rose-100 sm:aspect-[3/1]">
              <FaBookOpen className="text-4xl text-pink-300/80" aria-hidden />
            </div>
          )}

          <div className="px-5 py-8 sm:px-10 sm:py-10">
            <div className="mb-4 flex flex-wrap items-center gap-2 xl:hidden">
              {meta.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1 text-xs font-medium capitalize text-stone-700"
                >
                  {m.includes('min') ? <FaClock className="text-[10px] opacity-70" /> : null}
                  {m}
                </span>
              ))}
            </div>

            <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-4xl">
              {lessonTitleUi}
            </h1>

            {lesson.sessionNumber != null ? (
              <p className="mt-2 text-sm text-slate-500 xl:hidden">
                {sorted.length
                  ? t('reader.sessionOf', {
                      n: lesson.sessionNumber,
                      total: sorted.length,
                    })
                  : null}
              </p>
            ) : null}

            <div className="mt-8 max-w-none text-base leading-relaxed">
              {!blocks.length ? (
                <p className="rounded-xl border border-dashed border-stone-200 bg-stone-50/80 px-4 py-6 text-center text-slate-600">
                  {t('reader.emptyBody')}
                </p>
              ) : null}
              {blocks.map((block) => {
                if (block.type === 'tutorial_ribbon') {
                  return (
                    <div
                      key={block.id}
                      id={block.id}
                      className="mb-10 scroll-mt-24 rounded-2xl bg-gradient-to-r from-pink-600 via-fuchsia-600 to-rose-600 px-5 py-3 text-center shadow-md"
                    >
                      <p className="text-sm font-semibold uppercase tracking-widest text-white/95">
                        {block.title}
                      </p>
                    </div>
                  );
                }
                if (block.type === 'tutorial_intro') {
                  return (
                    <section
                      key={block.id}
                      id={block.id}
                      className="mb-12 scroll-mt-24 rounded-2xl border border-pink-100 bg-gradient-to-br from-pink-50/90 to-white px-6 py-8 shadow-sm sm:px-8"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-pink-700">
                        {block.title}
                      </p>
                      <div className="mt-4 max-w-none whitespace-pre-wrap text-base leading-relaxed text-slate-700">
                        {block.body}
                      </div>
                    </section>
                  );
                }
                if (block.type === 'tutorial_section') {
                  return (
                    <section
                      key={block.id}
                      id={block.id}
                      className="mb-12 scroll-mt-24 border-b border-stone-100 pb-12 last:border-0"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg bg-stone-900 text-sm font-bold text-white">
                          {block.sectionIndex}
                        </span>
                        <h2 className="font-display text-2xl font-semibold text-slate-900">
                          {block.title}
                        </h2>
                      </div>
                      {block.subtitle ? (
                        <p className="mt-2 text-lg font-medium text-pink-800/90">{block.subtitle}</p>
                      ) : null}
                      <div className="mt-5 max-w-none whitespace-pre-wrap text-base leading-[1.75] text-slate-700">
                        {block.body}
                      </div>
                    </section>
                  );
                }
                if (block.type === 'tutorial_summary') {
                  return (
                    <section
                      key={block.id}
                      id={block.id}
                      className="mb-12 scroll-mt-24 rounded-2xl border border-pink-200 bg-gradient-to-br from-pink-50/90 to-white px-6 py-8 shadow-sm sm:px-8"
                    >
                      <h2 className="font-display text-xl font-semibold text-pink-950">
                        {block.title}
                      </h2>
                      <div className="mt-4 max-w-none whitespace-pre-wrap text-base leading-relaxed text-slate-700">
                        {block.body}
                      </div>
                    </section>
                  );
                }
                if (block.type === 'text') {
                  return (
                    <section key={block.id} id={block.id} className="mb-10 scroll-mt-24">
                      <h2 className="font-display text-xl font-semibold text-slate-900">
                        {block.title}
                      </h2>
                      <p className="mt-3 whitespace-pre-wrap leading-relaxed text-slate-600">
                        {block.body}
                      </p>
                    </section>
                  );
                }
                if (block.type === 'list') {
                  return (
                    <section key={block.id} id={block.id} className="mb-10 scroll-mt-24">
                      <h2 className="font-display text-xl font-semibold text-slate-900">
                        {block.title}
                      </h2>
                      <ul className="mt-4 space-y-3 border-l-2 border-pink-200 pl-5">
                        {block.items.map((item, i) => (
                          <li
                            key={i}
                            className="leading-relaxed text-slate-600 marker:text-pink-500"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                }
                if (block.type === 'assignment') {
                  const a = block.assignment;
                  return (
                    <section
                      key={block.id}
                      id={block.id}
                      className="mb-10 scroll-mt-24 rounded-2xl border border-pink-200/80 bg-gradient-to-br from-pink-50/90 to-white p-6 shadow-sm"
                    >
                      <h2 className="font-display text-xl font-semibold text-pink-950">
                        {block.title}
                      </h2>
                      {a.brief ? (
                        <p className="mt-3 leading-relaxed text-slate-700">{a.brief}</p>
                      ) : null}
                      {a.instructions ? (
                        <div className="mt-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-pink-800/80">
                            {t('reader.instructions')}
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-slate-700">{a.instructions}</p>
                        </div>
                      ) : null}
                      {a.deliverables ? (
                        <div className="mt-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-pink-800/80">
                            {t('reader.deliverables')}
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-slate-700">{a.deliverables}</p>
                        </div>
                      ) : null}
                      {a.rubric ? (
                        <div className="mt-4 rounded-lg bg-white/70 p-3 text-sm text-slate-600">
                          <span className="font-medium text-slate-800">{t('reader.rubric')}: </span>
                          {a.rubric}
                        </div>
                      ) : null}
                    </section>
                  );
                }
                return null;
              })}
            </div>

            {showResourcesSection ? (
              <section
                id="lesson-resources"
                className="mt-10 min-w-0 scroll-mt-24 overflow-hidden border-t border-stone-100 pt-8"
              >
                <h2 className="font-display text-lg font-semibold text-slate-900">
                  {t('reader.resources')}
                </h2>

                {lessonVideoUrls.length > 0 ? (
                  <div className="mt-6 space-y-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t('reader.sessionVideos')}
                    </h3>
                    <div className="space-y-6">
                      {lessonVideoUrls.map((url, i) => {
                        const embed = getYouTubeEmbedUrl(url);
                        const n = lessonVideoUrls.length > 1 ? ` ${i + 1}` : '';
                        return (
                            <div key={`${url}-${i}`} className="min-w-0 space-y-3">
                            {embed ? (
                              <>
                                <div className="overflow-hidden rounded-2xl border border-stone-200 shadow-md ring-1 ring-stone-900/[0.04]">
                                  <div className="aspect-video w-full bg-black">
                                    <iframe
                                      title={`${t('reader.lessonVideo')}${n}`}
                                      src={embed}
                                      className="h-full w-full"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  </div>
                                </div>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex max-w-full items-center gap-1.5 break-all text-sm font-medium text-blue-800 hover:text-blue-950 hover:underline"
                                >
                                  {t('reader.watchVideo')}
                                  {n.trim()}
                                  <FaExternalLinkAlt className="text-[10px] opacity-70" />
                                </a>
                              </>
                            ) : (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex max-w-full items-center gap-2 rounded-xl border border-pink-200 bg-pink-50/90 px-4 py-2.5 text-sm font-medium text-pink-950 transition hover:bg-pink-100"
                              >
                                <FaVideo className="shrink-0" />
                                <span className="min-w-0 break-words">{`${t('reader.watchVideo')}${n}`}</span>
                                <FaExternalLinkAlt className="shrink-0 text-xs opacity-70" />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {lessonAttachments.length > 0 ? (
                  <div
                    className={`space-y-4 ${lessonVideoUrls.length ? 'mt-10' : 'mt-6'}`}
                  >
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t('reader.sessionAttachments')}
                    </h3>
                    <ul className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                      {lessonAttachments.map((att) => (
                        <li key={att.id} className="min-w-0">
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-full min-h-[4.5rem] min-w-0 max-w-full flex-col justify-between overflow-hidden rounded-2xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-4 shadow-sm transition hover:border-pink-200/80 hover:shadow-md"
                          >
                            <span className="flex min-w-0 items-start gap-3">
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pink-100 text-pink-700">
                                {att.kind === 'file' ? (
                                  <FaFileAlt className="text-lg" />
                                ) : (
                                  <FaLink className="text-lg" />
                                )}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="line-clamp-2 break-words font-medium text-slate-900">
                                  {att.title}
                                </span>
                                <span className="mt-1 line-clamp-1 break-all text-xs text-slate-500">
                                  {att.url}
                                </span>
                              </span>
                            </span>
                            <span className="mt-3 inline-flex max-w-full items-center gap-1 text-xs font-semibold text-pink-700">
                              <FaPaperclip className="shrink-0 text-[10px]" />
                              {t('reader.openAttachment')}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {(lesson.assignmentUrl || lesson.tuteUrl) && (
                  <div
                    className={`flex min-w-0 flex-wrap gap-3 ${lessonVideoUrls.length || lessonAttachments.length ? 'mt-8' : 'mt-4'}`}
                  >
                    {lesson.assignmentUrl ? (
                      <a
                        href={lesson.assignmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex max-w-full items-center gap-2 rounded-xl border border-pink-200 bg-pink-50 px-4 py-2.5 text-sm font-medium text-pink-900 transition hover:bg-pink-100"
                      >
                        <FaFileDownload className="shrink-0" />
                        <span className="min-w-0 break-words">{t('reader.assignmentFile')}</span>
                      </a>
                    ) : null}
                    {lesson.tuteUrl ? (
                      <a
                        href={lesson.tuteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex max-w-full items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800 transition hover:bg-emerald-100"
                      >
                        <FaLink className="shrink-0" />
                        <span className="min-w-0 break-words">{t('reader.tutorialLink')}</span>
                      </a>
                    ) : null}
                  </div>
                )}
              </section>
            ) : null}

            <footer className="mt-12 flex flex-col gap-6 border-t border-stone-100 pt-8 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {prev ? (
                  <Link
                    to={lessonHref(prev)}
                    className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-stone-100"
                  >
                    <FaArrowLeft className="text-xs" />
                    {t('reader.previous')}
                  </Link>
                ) : null}
                {next ? (
                  <Link
                    to={lessonHref(next)}
                    className="inline-flex items-center gap-2 rounded-xl border border-pink-200 bg-pink-50 px-4 py-2 text-sm font-medium text-pink-900 transition hover:bg-pink-100"
                  >
                    {t('reader.next')}
                    <FaArrowRight className="text-xs" />
                  </Link>
                ) : null}
              </div>

              {isStudent && canStudy ? (
                <button
                  type="button"
                  onClick={handleToggleComplete}
                  className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition ${
                    isCompleted
                      ? 'bg-emerald-100 text-emerald-900 ring-2 ring-emerald-300/60'
                      : 'bg-white text-slate-700 ring-2 ring-stone-200 hover:bg-stone-50'
                  }`}
                >
                  {isCompleted ? (
                    <FaCheckCircle className="text-emerald-600" />
                  ) : (
                    <FaRegCircle className="text-slate-400" />
                  )}
                  {isCompleted ? t('reader.completed') : t('reader.markComplete')}
                </button>
              ) : null}
            </footer>
          </div>
        </article>
          </div>
        </div>
      </div>

      {showAdminCourseBar ? (
        <footer
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 border-l-0 bg-gradient-to-r from-slate-950 via-slate-900 to-fuchsia-950 px-2.5 py-1.5 shadow-[0_-8px_24px_-6px_rgba(0,0,0,0.35)] sm:px-6 sm:py-2.5 md:left-64 md:z-50 md:border-l-2 md:border-gray-800 md:shadow-[inset_1px_0_0_rgba(255,255,255,0.04),0_-12px_40px_-8px_rgba(15,23,42,0.45)]"
          role="contentinfo"
          aria-label="Course switcher"
        >
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-1 sm:flex-row sm:items-end sm:gap-4">
            <div className="min-w-0 flex-1">
              <label
                htmlFor="lesson-reader-admin-course"
                className="mb-0.5 hidden text-[10px] font-medium uppercase tracking-wide text-slate-300 sm:mb-1 sm:block sm:text-[11px]"
              >
                {t('reader.switchCourse')}
              </label>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                <select
                  id="lesson-reader-admin-course"
                  value={courseId}
                  onChange={(e) => handleAdminCourseSwitch(e.target.value)}
                  className="w-full min-w-0 max-w-xl cursor-pointer rounded-md border border-white/20 bg-white/10 px-2 py-1 text-[11px] font-medium text-white shadow-inner backdrop-blur-sm focus:border-blue-300/50 focus:outline-none focus:ring-1 focus:ring-blue-400/40 sm:max-w-2xl sm:rounded-lg sm:px-3 sm:py-2 sm:text-sm sm:focus:ring-2"
                >
                  {sortedAdminCourses.map((c) => (
                    <option key={c.id} value={c.id} className="bg-slate-900 text-white">
                      {c.name}
                      {c.courseCode ? ` · ${c.courseCode}` : ''}
                    </option>
                  ))}
                </select>
                {adminCourseMeta ? (
                  <span className="hidden text-xs text-slate-400 sm:inline sm:max-w-[220px] sm:truncate">
                    {adminCourseMeta.academicLevel || 'Level not set'}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </footer>
      ) : null}
    </div>
  );
};

export default LessonReaderPage;
